import {
  reactive,
  ref,
  Ref,
  toRef,
  Component,
  AllowedComponentProps,
  VNode,
  VNodeProps,
  h,
  render,
  nextTick,
  ComponentInternalInstance,
  ComponentPublicInstance,
  StyleValue,
  Slot
} from 'vue';

import type { MaybeRef } from '@vueuse/core';

import {
  ElButton,
  ElMessageBox,
  ElMessageBoxOptions,
  ElNotification,
  ElMessage
} from 'element-plus';

import EyMessageBox, { EyMessageBoxOptions } from '../common/popup/messageBox';

import { IgnoreError } from './Errors';
import { showError } from './Common';
import { wrapOnClick } from './CommonInternal';
import { getMainAppContext } from './Plugin';
import { isFunction, noop } from 'lodash-es';

import DrawerContainer from '../common/popup/DrawerContainer.vue';
import PopoverContainer from '../common/popup/PopoverContainer.vue';

type CallbackType =
  | ((...args: any[]) => void)
  | Record<string, (...args: any[]) => void>;

export interface PopupCtx<T> {
  done: (result?: T) => void; // popup内部调用，关闭并返回值
  callback: CallbackType; // popup内部调用，发送通知，但不关闭popup

  popupMode: 'dialog' | 'drawer' | 'popover'; // 弹出方式

  // 由popup注册，点击popup框架上的"确定"按钮处罚
  confirm?: (options: { loading: Ref<boolean> }) => void;

  beforeClose?: () => Promise<boolean>; // popup关闭前触发, 返回false可阻止关闭
}

interface PopupSupport<T> {
  popupCtx?: PopupCtx<T>;
}

// popup组件创建成功, 获取实现的回调
type Hook = (component: ComponentInternalInstance) => void;

// 通过组件类型，推断属性类型
type ComponentProps<C extends Component> = C extends new (...args: any) => any
  ? Omit<
      InstanceType<C>['$props'],
      keyof VNodeProps | keyof AllowedComponentProps
    >
  : never;

// 组件的popup返回值类型
type PopupResult<C extends Component> =
  ComponentProps<C> extends PopupSupport<any>
    ? NonNullable<ComponentProps<C>['popupCtx']> extends PopupCtx<infer R>
      ? R
      : never
    : never;

/**
 * 显示 dialog 返回dialog中的响应值
 * @param component 组件
 * @param props  组件属性, {key:value}格式
 * @param param2 dialog参数
 * @returns
 */
export function showDialog<C extends Component>(
  component: C,
  props: ComponentProps<C>,
  {
    title = '请选择',
    defaultFooter = true, // 是否显示footer按钮(确定、取消)
    confirmButtonText = '确定', // 确定按钮的文本
    cancelButtonText = '取消', // 取消按钮的文档
    showCancelButton = true,
    showClose = true, // 是否显示关闭按钮
    modal = true, // 显示mask
    closeOnClickModal = false, // 点击mask自动关闭
    size = '', // 长宽
    customStyle = { width: '60%', height: '60%' },
    callback = noop as CallbackType // dialog随时回调的函数
  } = {}
): Promise<PopupResult<C>> {
  if (size) {
    customStyle = { width: size, height: size };
  }

  type R = PopupResult<C>;
  // 是否点击了confirm
  let isConfirm = false;
  // dialog 返回值
  let resultData: R | undefined = undefined;

  let headerSlot: Slot | undefined;
  let footerSlot: Slot | undefined;

  // 传递给dialog的上下文
  const popupCtx: PopupCtx<R> & {
    // PopupContainer 调用此方法
    updateSlot: (header?: Slot, footer?: Slot) => void;
  } = {
    popupMode: 'dialog',
    done: (r?: R) => {
      resultData = r;
      instance?.doClose?.();

      if (!isConfirm) {
        if (r === undefined) {
          (options as any).onAction('cancel');
        } else {
          (options as any).onAction('confirm');
        }
      }
    },
    callback,

    // PopupContainer 调用此方法
    updateSlot: (header?: Slot, footer?: Slot) => {
      if (instance) {
        headerSlot = undefined;
        footerSlot = undefined;

        instance.header = header;
        instance.footer = footer;
      } else {
        headerSlot = header;
        footerSlot = footer;
      }
    }
  };

  const options: EyMessageBoxOptions & { modalClass: string } = reactive({
    title,
    confirmButtonText,
    cancelButtonText,
    showCancelButton,
    showClose,
    customClass: 'easy-msgbox-dlg',
    customStyle: customStyle,
    modalClass:
      'easy-msgbox-modal easy-popup' +
      (modal ? '' : ' easy-msgbox-transparent'),
    closeOnClickModal,
    closeOnPressEscape: false,
    closeOnHashChange: true,
    draggable: true,
    noFooter: !defaultFooter,

    message: h(component as any, {
      ...props,
      popupCtx
    }),

    beforeClose: async (action, instance, done) => {
      if (action === 'confirm') {
        isConfirm = true;
        try {
          await popupCtx.confirm?.({ loading });
        } catch (error: any) {
          showError(error);
        } finally {
          loading.value = false;
        }
      } else {
        // 判断是否允许关闭
        if (popupCtx.beforeClose && !(await popupCtx.beforeClose())) {
          return;
        }

        done();
      }
    }
  });

  let loading = ref(false);

  // 拿到组件的vm
  let instance: ComponentPublicInstance<{
    doClose: () => void;
    title: string;
    header?: Slot;
    footer?: Slot;
  }>;

  const result = EyMessageBox(
    options,
    (
      vm: ComponentPublicInstance<{
        doClose: () => void;
        title: string;
        header?: Slot;
        footer?: Slot;
      }>
    ) => {
      instance = vm;

      loading = toRef(instance as any, 'confirmButtonLoading') as Ref<boolean>;

      // 更新插槽
      if (headerSlot || footerSlot) {
        popupCtx.updateSlot(headerSlot, footerSlot);
      }
    }
  );

  return result
    .then(() => {
      if (resultData !== undefined) {
        // 返回dialog结果
        return resultData;
      }

      throw new IgnoreError('cancel');
    })
    .catch((reason: any) => {
      if (resultData !== undefined) {
        // 返dialog结果
        return resultData;
      }

      throw new IgnoreError(reason);
    });
}

export type DrawerDirection = 'ltr' | 'rtl' | 'ttb' | 'btt';

// drawer
export function showDrawer<C extends Component>(
  component: C,
  props: ComponentProps<C>,
  {
    title = '请选择',
    direction = 'rtl' as DrawerDirection,
    modal = false, // 显示mask
    closeOnClickModal = false, // 点击mask自动关闭
    showClose = true, // 是否显示关闭按钮
    defaultFooter = false, // 是否显示footer按钮(确定、取消)
    confirmButtonText = '确定', // 确定按钮的文本
    cancelButtonText = '取消', // 取消按钮的文本
    size = '30%', // drawer宽度
    callback = noop as CallbackType // drawer随时回调的函数
  } = {}
): Promise<PopupResult<C>> {
  type R = PopupResult<C>;

  let responsed = false;
  //
  return new Promise<R>((resolve, reject) => {
    const popupCtx: PopupCtx<R> & {
      // PopupContainer 调用此方法
      updateSlot: (title?: Slot, footer?: Slot) => void;
    } = {
      popupMode: 'drawer',
      done: (r?: R) => {
        if (responsed) return;
        responsed = true;

        handleClose();

        if (r === undefined) {
          reject('cancel');
          return;
        }

        resolve(r);
      },
      callback,

      // PopupContainer 调用此方法
      updateSlot: (title?: Slot, footer?: Slot) => {
        titleSlot.value = title;
        footerSlot.value = footer || defaultFooterSlot;
      }
    };

    // jsx title处理
    const titleSlot = ref<Slot>();

    // 默认按钮的loading状态
    const loading = ref(false);

    const defaultFooterSlot = defaultFooter
      ? () => [
          h(
            ElButton,
            {
              onClick: () => handleClose()
            },
            () => cancelButtonText
          ),
          h(
            ElButton,
            {
              type: 'primary',
              loading: loading.value,
              onClick: wrapOnClick(async () => {
                try {
                  return await popupCtx.confirm?.({ loading });
                } finally {
                  loading.value = false;
                }
              })
            },
            () => confirmButtonText
          )
        ]
      : undefined;

    const footerSlot = ref<Slot | undefined>(defaultFooterSlot);

    const destory = () => {
      if (container) {
        render(null, container);
        container = null;
      }

      popupCtx.done(undefined);
    };

    const beforeDrawerClose = async (done: (cancel?: boolean) => void) => {
      try {
        if (popupCtx.beforeClose && !(await popupCtx.beforeClose())) {
          return;
        }
        done();
      } catch (e) {
        showError(e);
      }
    };

    // create vnode
    const vnode = h(
      DrawerContainer,
      {
        title,
        direction,
        modal: closeOnClickModal || modal,
        closeOnClickModal,
        showClose,
        size,
        popupCtx,
        destory,
        beforeClose: beforeDrawerClose,

        defaultSlot: () =>
          h(component, {
            ...props,
            popupCtx
          } as any),

        headerSlot: () => titleSlot,
        footerSlot: () => footerSlot
      } as any,
      {}
    );

    vnode.appContext = getMainAppContext();

    let container: HTMLElement | null = document.createElement('div');
    render(vnode, container);
    const instance = vnode.component;

    const handleClose = () => (instance as any)?.exposed?.handleClose?.();

    document.body.appendChild(container.firstElementChild!);
  }).catch((reason) => {
    throw new IgnoreError(reason);
  });
}

export type PopoverPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'auto'
  | 'auto-start'
  | 'auto-end';

/**
 * Popover
 * @param component 嵌入的组件
 * @param props 传入参数
 * @param virtualRef 位置
 * @param param3 ElPopover参数
 */
export function showPopover<C extends Component>(
  component: C,
  props: ComponentProps<C>,
  virtualRef: MaybeRef<any>,
  {
    placement = 'bottom' as PopoverPlacement,
    width = '150px' as string | number,
    defaultFooter = true,
    callback = noop as CallbackType, // dialog随时回调的函数
    focus = true, // 自动设置焦点
    showCloseModal = false, // 显示遮罩层
    closeOnBlur = true, // 失去焦点时自动关闭,focus=true时有效
    popperStyle = undefined as StyleValue | undefined,
    hook = undefined as unknown as Hook
  } = {}
): Promise<PopupResult<C>> {
  type R = PopupResult<C>;
  //
  return new Promise<R>((resolve, reject) => {
    const popupCtx: PopupCtx<R> & {
      // PopupContainer 调用此方法
      updateSlot: (title?: Slot, footer?: Slot) => void;
    } = {
      popupMode: 'popover',
      done: (r?: R) => {
        if (!container) return;

        try {
          render(null, container);
          // 有bug? 实测element未删除, 此处补一下
          // document.body.removeChild(el!);
          container = null;
          el = null;
        } finally {
          if (r === undefined) {
            reject('cancel');
            return;
          }
          resolve(r);
        }
      },

      callback,

      // PopupContainer 调用此方法
      updateSlot: (_title?: Slot, footer?: Slot) => {
        footerSlot.value = footer;
      }
    };

    // footer"确定"按钮
    const footerSlot = ref<Slot | undefined>();

    // create vnode
    const vnode = h(
      PopoverContainer,
      {
        virtualRef,
        placement,
        width,
        popperStyle,
        showCloseModal,
        closeOnBlur: closeOnBlur,

        defaultSlot: () =>
          h(component, {
            ...props,
            popupCtx
          } as any),

        defaultFooter,
        footerSlot: () => footerSlot,
        popupCtx
      } as any,

      {}
    );

    vnode.appContext = getMainAppContext();

    let container: HTMLElement | null = document.createElement('div');
    render(vnode, container);

    let el: HTMLElement | null = container.firstElementChild! as HTMLElement;
    document.body.appendChild(el);

    if (hook) {
      hook(vnode.component!);
    }

    // 设置输入焦点
    if (focus) nextTick(() => el?.focus());
  }).catch((reason) => {
    throw new IgnoreError(reason);
  });
}

export function usePopup<T>(props: { popupCtx?: PopupCtx<T> }) {
  return {
    done(result?: T) {
      props.popupCtx?.done(result);
    },

    call(...args: any[]) {
      const callback = props.popupCtx?.callback;
      if (callback) {
        if (isFunction(callback)) {
          callback(...args);
        } else {
          const action = args.shift() as string;
          callback[action]?.(...args);
        }
      }
    },

    // “确定”按钮 处理函数
    resultDef(callback: (options: { loading: Ref<boolean> }) => void) {
      if (props.popupCtx) {
        props.popupCtx.confirm = callback;
      }
    },

    // popup关闭前调用, 可阻止关闭
    beforeClose(callback: () => Promise<boolean>) {
      if (props.popupCtx) {
        props.popupCtx.beforeClose = callback;
      }
    },

    popupMode: props.popupCtx?.popupMode,

    // 当前组件以dialog方式显示
    isDialog: props.popupCtx?.popupMode == 'dialog',

    // 当前组件以drawer方式显示
    isDrawer: props.popupCtx?.popupMode == 'drawer',

    // 当前组件是popover方式显示
    isPopover: props.popupCtx?.popupMode == 'popover',

    // 当前组件不以popup方式显示
    isNonPopup: props.popupCtx?.popupMode === undefined
  };
}

////////////////////////////////////////////////////////////////////////
interface NotificationOptions {
  type?: 'success' | 'info' | 'warning' | 'error';
  title?: string;
  showClose?: boolean;
}

/**
 * 显示通知
 * @param message 消息内容
 * @param param1
 */
export function showNotification(
  message: string,
  { type = 'warning', title, showClose = false }: NotificationOptions = {}
) {
  ElNotification({
    type: type,
    title: title,
    message: message,
    showClose: showClose
  });
}

/**
 * 通知成功
 * @param message 显示的消息
 */
export function notifySuccess(message: string) {
  ElMessage({
    message: message,
    type: 'success'
  });
}

/**
 * 通知异常
 * @param message 显示的消息
 */
export function notifyError(message: string) {
  ElMessage({
    message: message,
    type: 'error'
  });
}

/**
 * 提示，并选择: 如果选“取消”则抛出IgnoreError异常
 * @param msg 提示消息
 * @param options
 */
export function confirm(
  msg: string | VNode | (() => VNode),
  options: ElMessageBoxOptions = {}
) {
  return ElMessageBox.confirm(msg, '', {
    customClass: 'easy-msgbox',
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
    draggable: true,
    ...options
  })
    .then(noop)
    .catch((reason) => {
      throw new IgnoreError(reason);
    });
}

/**
 * 弹出窗口输入
 * @param msg
 * @param options
 */
export function prompt(
  msg: string | VNode | (() => VNode),
  options: ElMessageBoxOptions = {}
) {
  return ElMessageBox.prompt(msg, '', {
    customClass: 'easy-msgbox',
    autofocus: true,
    draggable: true,
    closeOnClickModal: false,
    //inputPattern: /[^\\w].?/,
    //inputErrorMessage: '请输入...',
    ...options
  } as ElMessageBoxOptions)
    .then((result) => result.value)
    .catch((reason) => {
      throw new IgnoreError(reason);
    });
}
